import {
  isInvalidSupOrgTeamId,
  sanitizeRepositoryEntityName,
} from '@internal/plugin-watchtower-common';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import InfoIcon from '@mui/icons-material/Info';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { InfoCard } from '@backstage/core-components';

import {
  makeStyles,
  Theme,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Button,
  Collapse,
  Snackbar,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DateRangeIcon from '@material-ui/icons/DateRange';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';
import WarningIcon from '@material-ui/icons/Warning';
import { Pagination } from '@material-ui/lab';

import { useGetFixAction } from '../../hooks';
import { CategoryScore, CheckV2, FixableCategory } from '../../types';
import { useScorecardLinkBuilder } from '../../utils';
import {
  getBadgeColor,
  getSubStrForLength,
  getChildTeamScoreColor,
  normalizeTeamId,
  useTeamNames,
} from '../../utils/scorecardCategoryDetailsUtils';
import { getTagKeyForCheck } from '../fixes/ResourceGroupTagFix/resourceGroupTags';
import {
  ResourceGroupBreakdown,
  isResourceGroupList,
} from './ResourceGroupBreakdown';

const useStyles = makeStyles((theme: Theme) => ({
  categoryCard: {
    minHeight: 'auto',
    padding: '12px 16px',
    marginBottom: theme.spacing(1.5),
    borderRadius: '16px !important',
  },
  mainCard: { margin: 0 },
  categoryCardContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  categoryCardContentLeft: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  scoreBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    minWidth: 120,
  },
  checkList: {
    border: `1px solid ${theme.palette.border}`,
    borderRadius: '6px',
    marginBottom: '8px',
    background: `${theme.palette.background.paper} !important`,
  },
  infoIcon: {
    marginLeft: 4,
    verticalAlign: 'middle',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  childrenSection: { marginBottom: theme.spacing(2.5) },
  sectionTitle: {
    marginBottom: theme.spacing(0.75),
    fontWeight: 600,
    fontSize: '1rem',
  },
  sectionHelperText: {
    marginBottom: theme.spacing(1.25),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  childTeamCard: {
    padding: theme.spacing(1.25),
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': { boxShadow: theme.shadows[2], transform: 'translateY(-1px)' },
  },
  childTeamCardStatic: {
    cursor: 'default',
    '&:hover': { boxShadow: 'none', transform: 'none' },
  },
  childTeamHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  childTeamName: {
    fontWeight: 500,
    fontSize: '0.85rem',
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  childTeamScore: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1,
    textAlign: 'right',
  },
  childTeamId: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    marginTop: 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  childTeamBadge: {
    marginTop: theme.spacing(0.75),
    '& .MuiChip-label': {
      fontSize: '0.65rem',
      fontWeight: 500,
      padding: '0 8px',
    },
  },
  periodBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    padding: '4px 10px',
    borderRadius: 6,
    width: 'fit-content',
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  descriptionText: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.8rem',
    lineHeight: 1.4,
    marginTop: 6,
    wordBreak: 'break-word',
  },
  statBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 4,
    background: theme.palette.background.default,
  },
  expandableSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  expandableContent: {
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
  },
  alertBox: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    gap: theme.spacing(1),
  },
  searchFilterBar: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 500,
    overflowX: 'auto',
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  tableRow: { '&:hover': { backgroundColor: theme.palette.action.hover } },
  detailValue: {
    fontSize: '0.875rem',
    display: 'block',
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  emptyState: { textAlign: 'center', padding: theme.spacing(4) },
  footerStats: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  previewSection: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  showMoreButton: { marginTop: theme.spacing(1), width: '100%' },
  checkHeader: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
  },
}));

const ITEMS_PER_PAGE = 50;
const SKIP_ALWAYS = new Set(['check_status', '_checkName', 'check_name']);
const NUMERIC_SORT_COLS = new Set(['value', 'success_rate']);

const RESOURCE_NAME_PRIORITY = [
  'jira_team_name',
  'project_key',
  'account',
  'asset_name',
  'repository_name',
  'host_name',
];

const VELOCITY_SECONDS_CHECKS = new Set([
  'p75_time_to_pickup_pr',
  'p75_time_to_review_pr',
  'p75_time_to_merge_pr',
  'p75_time_to_open_pr',
  'p75_lead_time_for_changes',
]);
const PR_SIZE_CHECK = 'p75_pr_size';

const humaniseKey = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const COLUMN_HEADER_OVERRIDES: Record<string, string> = {
  success_rate: 'Success Rate',
  workflow_run_count: 'Runs',
  workflow_success_count: 'Success',
  workflow_failure_count: 'Failures',
};

const columnHeaderLabel = (key: string) =>
  COLUMN_HEADER_OVERRIDES[key] ?? humaniseKey(key);

const isKTLOCategory = (name?: string) => name?.toUpperCase() === 'KTLO';

const formatRefreshPeriod = (seconds: number): string => {
  if (seconds === 0) return 'Immediate';
  if (seconds % 86400 === 0) {
    const days = seconds / 86400;
    if (days === 1) return 'Daily';
    if (days === 7) return 'Weekly';
    return `Every ${days} days`;
  }
  if (seconds % 3600 === 0) return `Every ${seconds / 3600}h`;
  return `Every ${Math.round(seconds / 60)}m`;
};

const SIX_MONTHS_DAYS = 182;
const ONE_YEAR_DAYS = 365;

const daysSince = (iso?: string): number | null => {
  if (!iso) return null;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return null;
  return Math.floor((Date.now() - time) / 86400000);
};

const formatRelativeDate = (iso?: string): string => {
  const days = daysSince(iso);
  if (days === null) return 'N/A';
  if (days <= 0) return 'today';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return months > 0 ? `${years}y ${months}mo ago` : `${years}y ago`;
};

const formatPeriodDate = (iso?: string): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const formatPeriodRange = (start?: string, end?: string): string | null => {
  const s = formatPeriodDate(start);
  const e = formatPeriodDate(end);
  if (s && e) return s === e ? s : `${s} – ${e}`;
  return s ?? e;
};

const getIconForCheck = (pass: boolean) =>
  pass ? (
    <TaskAltRoundedIcon color="success" fontSize="small" />
  ) : (
    <HighlightOffRoundedIcon color="error" fontSize="small" />
  );

const getSortArrow = (activeCol: string, col: string, dir: 'asc' | 'desc') => {
  if (activeCol !== col) return '';
  return dir === 'desc' ? '↓' : '↑';
};

const displayValue = (
  value: number | undefined | null,
  isPercentage = false,
) => {
  if (value === null || value === undefined || value === -1) return 'N/A';
  return isPercentage ? `${(value * 100).toFixed(0)}%` : value;
};

const formatCellValue = (value: any): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'N/A';
    return `${value.length} item${value.length !== 1 ? 's' : ''}`;
  }
  if (typeof value === 'object')
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ');
  return String(value);
};

// Nested values (e.g. untagged resource groups with their subscription) are
// matched on their values only, so key names such as `account_id` never match.
const toSearchText = (value: any): string =>
  value !== null && typeof value === 'object'
    ? Object.values(value).map(toSearchText).join(' ')
    : String(value);

const getValueColumnHeader = (checkName: string) => {
  if (checkName === PR_SIZE_CHECK) return 'Value (LoC)';
  if (VELOCITY_SECONDS_CHECKS.has(checkName)) return 'Value (h)';
  return 'Value';
};

const getExpandableDetails = (
  resource: any,
  tableColumns: string[],
): Record<string, any> => {
  const result: Record<string, any> = {};
  tableColumns.forEach(key => {
    const val = resource[key];
    if (val === null || val === undefined) return;
    if (Array.isArray(val) && val.length > 0) result[key] = val;
  });
  return result;
};

const formatVelocityValue = (
  checkName: string,
  value: any,
  fallbackToHours = false,
): string => {
  if (value === null || value === undefined || value === -1) return 'N/A';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  if (checkName === PR_SIZE_CHECK) return `${num}`;
  if (VELOCITY_SECONDS_CHECKS.has(checkName))
    return `${(num / 3600).toFixed(2)}`;
  if (fallbackToHours && checkName !== PR_SIZE_CHECK)
    return `${(num / 3600).toFixed(2)}`;
  return String(value);
};

const getResourceNameKey = (displayKeys: string[]): string | null => {
  for (const field of RESOURCE_NAME_PRIORITY) {
    if (displayKeys.includes(field)) return field;
  }
  return (
    displayKeys.find(
      k =>
        k.includes('name') &&
        !k.includes('team') &&
        !k.includes('display_name') &&
        !k.includes('priority_name'),
    ) ?? null
  );
};

const getTableColumns = (
  displayKeys: string[],
  resourceNameKey: string | null,
) =>
  displayKeys.filter(
    k => !SKIP_ALWAYS.has(k) && k !== resourceNameKey && k !== 'team_id',
  );

const flattenCheckDetail = (item: any): any[] => {
  if (!item.checks || typeof item.checks !== 'object') {
    return [{ ...item, _checkName: item._checkName ?? item.check_name ?? '' }];
  }
  return (Object.entries(item.checks) as [string, Record<string, any>][]).map(
    ([checkName, payload]) => ({
      ...payload,
      check_status: payload.check_status ?? 'N/A',
      repository_name: item.repository_name,
      _checkName: checkName,
    }),
  );
};

const escapeCsv = (value: any): string => {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return /[",\n]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
};

const slugify = (value: string): string =>
  value
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-/, '')
    .replace(/-$/, '')
    .toLowerCase();

const buildRowsCsv = (
  rows: Record<string, any>[],
  leadingCols: string[] = [],
): string | null => {
  if (rows.length === 0) return null;

  const keys = new Set<string>(leadingCols);
  rows.forEach(row =>
    Object.keys(row).forEach(key => {
      if (!SKIP_ALWAYS.has(key)) keys.add(key);
    }),
  );

  const headers = Array.from(keys);
  const lines = [
    headers.map(escapeCsv).join(','),
    ...rows.map(row => headers.map(h => escapeCsv(row[h])).join(',')),
  ];
  return lines.join('\n');
};

const downloadCsv = (filename: string, csv: string) => {
  const bom = String.fromCharCode(0xfeff);
  const blob = new Blob([bom + csv], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface ChildTeam {
  team_id: string;
  aggregated_category_badge: string;
  aggregated_category_score_percentage: number;
  aggregated_category_score_proportion_dividend?: number;
  aggregated_category_score_proportion_denominator?: number;
  category_score_proportion_dividend?: number;
  category_score_proportion_denominator?: number;
}

type SortDir = 'asc' | 'desc';

const StatBox = ({
  pass,
  value,
  label,
}: {
  pass: boolean;
  value: React.ReactNode;
  label: string;
}) => {
  const classes = useStyles();
  return (
    <Paper elevation={0} className={classes.statBox}>
      <Box>{getIconForCheck(pass)}</Box>
      <Box style={{ textAlign: 'center' }}>
        <Typography
          variant="body1"
          style={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: 1 }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ fontSize: '0.7rem' }}
        >
          {label}
        </Typography>
      </Box>
    </Paper>
  );
};

const PassFailCounts = ({
  passed,
  failed,
  size = '0.7rem',
}: {
  passed: number;
  failed: number;
  size?: string;
}) => (
  <Box style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
    {[
      { pass: true, count: passed },
      { pass: false, count: failed },
    ].map(({ pass, count }) => (
      <Box
        key={String(pass)}
        style={{ display: 'flex', alignItems: 'center', gap: 3 }}
      >
        {getIconForCheck(pass)}
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ fontSize: size }}
        >
          {count}
        </Typography>
      </Box>
    ))}
  </Box>
);

const isWorkflowList = (values: any[]): boolean =>
  values.length > 0 &&
  typeof values[0] === 'object' &&
  values[0] !== null &&
  'workflow_name' in values[0];

const WorkflowBreakdown = ({ workflows }: { workflows: any[] }) => {
  const classes = useStyles();
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Workflow</TableCell>
          <TableCell align="right">Success Rate</TableCell>
          <TableCell align="right">Runs</TableCell>
          <TableCell align="right">Success</TableCell>
          <TableCell align="right">Failures</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {workflows.map((wf, i) => {
          const failures = Number(wf.workflow_failure_count ?? 0);
          return (
            <TableRow key={i} className={classes.tableRow}>
              <TableCell>
                {wf.workflow_file_url ? (
                  <Link
                    href={wf.workflow_file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '0.8rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                    }}
                  >
                    {wf.workflow_name ?? 'N/A'}
                    <OpenInNewIcon style={{ fontSize: '0.8rem' }} />
                  </Link>
                ) : (
                  <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                    {wf.workflow_name ?? 'N/A'}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                  {wf.success_rate ?? 'N/A'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                  {wf.workflow_run_count ?? 'N/A'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                  {wf.workflow_success_count ?? 'N/A'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  style={{
                    fontSize: '0.8rem',
                    color: failures > 0 ? '#EF4444' : undefined,
                    fontWeight: failures > 0 ? 600 : undefined,
                  }}
                >
                  {wf.workflow_failure_count ?? 'N/A'}
                </Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

/** Array fields with a known item shape get a table instead of a plain list. */
const getStructuredBreakdown = (
  values: any[],
  checkName: string,
): React.ReactNode => {
  if (isWorkflowList(values)) return <WorkflowBreakdown workflows={values} />;
  if (isResourceGroupList(values))
    return (
      <ResourceGroupBreakdown
        resourceGroups={values}
        tagKey={getTagKeyForCheck(checkName)}
      />
    );
  return null;
};

const ResourceRow = ({
  resource,
  index,
  resourceNameKey,
  tableColumns,
  FixComponent,
  entity,
  performDataRefresh,
  isVelocity,
  teamNames = {},
}: {
  resource: any;
  index: number;
  resourceNameKey: string | null;
  tableColumns: string[];
  FixComponent: any;
  entity: any;
  performDataRefresh?: () => void;
  isVelocity?: boolean;
  teamNames?: Record<string, string>;
}) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  let resourceDisplayName: string;
  if (resourceNameKey) {
    resourceDisplayName = formatCellValue(resource[resourceNameKey]);
  } else if (resource._checkName) {
    resourceDisplayName = humaniseKey(resource._checkName);
  } else {
    resourceDisplayName = `Resource ${index + 1}`;
  }
  const isRepo = resourceNameKey === 'repository_name';
  const repoName =
    isRepo && typeof resource[resourceNameKey] === 'string'
      ? resource[resourceNameKey].trim()
      : '';
  const subscriptionId: string | undefined =
    resource.subscription_id ?? resource.subscriptionId;
  let catalogUrl: string | null = null;
  if (isRepo && repoName) {
    // The catalog route is keyed by the entity name, which for edge-case repos
    // differs from the raw repository name; sanitize to reconstruct it (identity
    // for normal repos).
    catalogUrl = `/catalog/default/repository/${encodeURIComponent(
      sanitizeRepositoryEntityName(repoName),
    )}/engineering-insights`;
  } else if (subscriptionId) {
    catalogUrl = `/catalog/default/resource/subscription-${encodeURIComponent(
      subscriptionId,
    )}`;
  }

  const expandableDetails = useMemo(
    () => getExpandableDetails(resource, tableColumns),
    [resource, tableColumns],
  );
  const hasExpandable = Object.keys(expandableDetails).length > 0;
  const colSpan = tableColumns.length + 2;

  return (
    <>
      <TableRow className={classes.tableRow}>
        <TableCell>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {hasExpandable && (
              <IconButton
                size="small"
                onClick={() => setIsExpanded(prev => !prev)}
                style={{ padding: 2 }}
                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
              >
                {isExpanded ? (
                  <ExpandLessIcon style={{ fontSize: '0.9rem' }} />
                ) : (
                  <ExpandMoreIcon style={{ fontSize: '0.9rem' }} />
                )}
              </IconButton>
            )}
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {resourceDisplayName}
            </Typography>
            {catalogUrl && (
              <Tooltip title={`Open ${resourceDisplayName} in Catalog`} arrow>
                <IconButton
                  size="small"
                  component="a"
                  href={catalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ padding: 2 }}
                >
                  <OpenInNewIcon style={{ fontSize: '0.85rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>

        {tableColumns.map(key => {
          const val = resource[key];
          const isExpandableField = key in expandableDetails;
          const isUrl =
            typeof val === 'string' &&
            (val.startsWith('http://') || val.startsWith('https://')) &&
            (key === 'issue_url' || key === 'repository_url');

          let cellContent: React.ReactNode;
          if (isExpandableField) {
            cellContent = (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{
                  fontStyle: 'italic',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                }}
                onClick={() => setIsExpanded(prev => !prev)}
              >
                {formatCellValue(val)} {isExpanded ? '↑' : '↓'}
              </Typography>
            );
          } else if (isUrl) {
            if (key === 'repository_url') {
              const cleanUrl = val.replace('/settings/custom-properties', '');
              const displayUrl = cleanUrl.replace(/^https?:\/\//, '');
              cellContent = (
                <Tooltip title={cleanUrl} arrow placement="top-start">
                  <Link
                    href={cleanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '0.825rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      maxWidth: 280,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {displayUrl}
                    <OpenInNewIcon
                      style={{ fontSize: '0.8rem', flexShrink: 0 }}
                    />
                  </Link>
                </Tooltip>
              );
            } else {
              const issueKey = val.includes('/browse/')
                ? val.split('/browse/').pop() || 'Open'
                : 'Open';
              cellContent = (
                <Link
                  href={val}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {issueKey} <OpenInNewIcon style={{ fontSize: '0.85rem' }} />
                </Link>
              );
            }
          } else if (key === 'pushed_at') {
            const days = daysSince(val);
            const veryStale = days !== null && days >= ONE_YEAR_DAYS;
            const stale = days !== null && days >= SIX_MONTHS_DAYS;
            let pushedColor: string | undefined;
            if (veryStale) pushedColor = '#EF4444';
            else if (stale) pushedColor = '#F59E0B';
            cellContent = (
              <Tooltip
                title={val ? new Date(val).toLocaleString() : 'N/A'}
                arrow
                placement="top-start"
                enterDelay={500}
              >
                <Typography
                  variant="body2"
                  className={classes.detailValue}
                  style={{
                    color: pushedColor,
                    fontWeight: stale ? 600 : undefined,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatRelativeDate(val)}
                  {stale ? ' ⚠' : ''}
                </Typography>
              </Tooltip>
            );
          } else {
            const isTeamCol =
              key !== 'jira_team_name' && key.toLowerCase().includes('team');
            let displayVal: string;
            if (isTeamCol && val) {
              displayVal =
                teamNames[normalizeTeamId(String(val))] ?? formatCellValue(val);
            } else if (isVelocity && key === 'value') {
              displayVal = formatVelocityValue(
                resource._checkName ?? '',
                val,
                isVelocity && !resource._checkName,
              );
            } else {
              displayVal = formatCellValue(val);
            }
            cellContent = (
              <Tooltip
                title={displayVal}
                arrow
                placement="top-start"
                enterDelay={500}
              >
                <Typography variant="body2" className={classes.detailValue}>
                  {displayVal}
                </Typography>
              </Tooltip>
            );
          }

          return <TableCell key={key}>{cellContent}</TableCell>;
        })}

        <TableCell>
          {FixComponent && (
            <FixComponent
              context={resource}
              entity={entity}
              performDataRefresh={performDataRefresh}
            />
          )}
        </TableCell>
      </TableRow>

      {hasExpandable && (
        <TableRow>
          <TableCell colSpan={colSpan} style={{ padding: 0, borderBottom: 0 }}>
            <Collapse in={isExpanded} unmountOnExit>
              <Box
                style={{
                  padding: '8px 16px 12px 40px',
                  background: 'rgba(0,0,0,0.02)',
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                {Object.entries(expandableDetails).map(([key, values]) => (
                  <Box key={key} style={{ marginBottom: 8 }}>
                    <Typography
                      variant="caption"
                      style={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        display: 'block',
                        marginBottom: 4,
                        opacity: 0.6,
                      }}
                    >
                      {humaniseKey(key)} ({values.length})
                    </Typography>
                    {getStructuredBreakdown(
                      values as any[],
                      resource._checkName ?? resource.check_name ?? '',
                    ) ?? (
                      <Box
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        {(values as any[]).map((item, i) => {
                          const isUrl =
                            typeof item === 'string' &&
                            (item.startsWith('http://') ||
                              item.startsWith('https://'));
                          return (
                            <Typography
                              key={i}
                              variant="body2"
                              style={{ fontSize: '0.8rem' }}
                            >
                              {isUrl ? (
                                <Link
                                  href={item}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ wordBreak: 'break-all' }}
                                >
                                  {item}
                                </Link>
                              ) : (
                                formatCellValue(item)
                              )}
                            </Typography>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const CheckTableHead = ({
  resourceColKey,
  tableColumns,
  activeSortCol,
  activeSortDir,
  onSort,
  isVelocity,
  firstCheckName,
}: {
  resourceColKey: string;
  tableColumns: string[];
  activeSortCol: string;
  activeSortDir: SortDir;
  onSort: (col: string) => void;
  isVelocity: boolean;
  firstCheckName: string;
}) => {
  const sortCell = (
    col: string,
    label: React.ReactNode,
    { wrap = false }: { wrap?: boolean } = {},
  ) => (
    <TableCell
      key={col}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        // Data headers wrap: names such as "Untagged Resource Group Count" held
        // their column open on one line, pushing Actions off the visible area
        // even though the values underneath are only a few characters wide.
        ...(wrap
          ? { whiteSpace: 'normal', maxWidth: 130, lineHeight: 1.25 }
          : { whiteSpace: 'nowrap' }),
      }}
      onClick={() => onSort(col)}
    >
      {label} {getSortArrow(activeSortCol, col, activeSortDir)}
    </TableCell>
  );
  return (
    <TableHead>
      <TableRow>
        {sortCell(resourceColKey, 'Resource')}
        {tableColumns.map(key =>
          sortCell(
            key,
            isVelocity && key === 'value'
              ? getValueColumnHeader(firstCheckName)
              : columnHeaderLabel(key),
            { wrap: true },
          ),
        )}
        <TableCell>Actions</TableCell>
      </TableRow>
    </TableHead>
  );
};

function useCheckTableState() {
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTermState] = useState('');
  const [selectedTeam, setSelectedTeamState] = useState('all');
  const [showFullTable, setShowFullTable] = useState(false);

  const handleSort = useCallback(
    (col: string) => {
      if (sortCol === col) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDir('desc');
        setSortCol(col);
      }
      setCurrentPage(1);
    },
    [sortCol],
  );

  const setSearchTerm = useCallback((v: string) => {
    setSearchTermState(v);
    setCurrentPage(1);
  }, []);

  const setSelectedTeam = useCallback((v: string) => {
    setSelectedTeamState(v);
    setCurrentPage(1);
  }, []);

  const reset = useCallback(() => {
    setSearchTermState('');
    setSelectedTeamState('all');
    setShowFullTable(false);
    setCurrentPage(1);
  }, []);

  return {
    sortCol,
    sortDir,
    currentPage,
    searchTerm,
    selectedTeam,
    showFullTable,
    setShowFullTable,
    setSearchTerm,
    setSelectedTeam,
    setCurrentPage,
    handleSort,
    reset,
  };
}

const CheckRow = ({
  check,
  isExpanded,
  onToggle,
  isVelocity,
  isKTLO,
  FixComponent,
  entity,
  performDataRefresh,
  availableTeams,
  teamNames,
  categoryName,
}: {
  check: CheckV2;
  isExpanded: boolean;
  onToggle: () => void;
  isVelocity: boolean;
  isKTLO: boolean;
  FixComponent: any;
  entity: any;
  performDataRefresh?: () => void;
  availableTeams: string[];
  teamNames: Record<string, string>;
  categoryName?: string;
}) => {
  const classes = useStyles();
  const state = useCheckTableState();

  const ktloDetails = useMemo(() => {
    if (!isKTLO) return [];
    if (check.checkDetails?.length > 0) return check.checkDetails;
    return [check];
  }, [isKTLO, check]);

  const flattenedDetails = useMemo(
    () => (isKTLO ? [] : check.checkDetails.flatMap(flattenCheckDetail)),
    [isKTLO, check.checkDetails],
  );

  const failedResources = useMemo(
    () =>
      isKTLO ? [] : flattenedDetails.filter(r => r.check_status === 'failed'),
    [isKTLO, flattenedDetails],
  );

  const displayKeys = useMemo(
    () =>
      failedResources.length > 0
        ? Object.keys(failedResources[0]).filter(k => !SKIP_ALWAYS.has(k))
        : [],
    [failedResources],
  );

  const resourceNameKey = useMemo(
    () => getResourceNameKey(displayKeys),
    [displayKeys],
  );
  const tableColumns = useMemo(() => {
    const cols = getTableColumns(displayKeys, resourceNameKey);
    return [
      ...cols.filter(c => c.endsWith('_url') || c === 'url'),
      ...cols.filter(c => !c.endsWith('_url') && c !== 'url'),
    ];
  }, [displayKeys, resourceNameKey]);

  const defaultSortCol =
    tableColumns.find(c => NUMERIC_SORT_COLS.has(c)) ?? 'repository_name';
  const activeSortCol = state.sortCol || defaultSortCol;
  const activeSortDir: SortDir = (() => {
    if (state.sortCol) return state.sortDir;
    return defaultSortCol === 'value' ? 'desc' : 'asc';
  })();
  const resourceColKey = resourceNameKey ?? 'repository_name';

  const filteredResources = useMemo(() => {
    const searchLower = state.searchTerm.toLowerCase();
    return failedResources.filter(resource => {
      const matchesSearch =
        !searchLower ||
        Object.entries(resource)
          .filter(([key]) => key !== 'check_status')
          .some(([, value]) =>
            toSearchText(value).toLowerCase().includes(searchLower),
          );
      const matchesTeam =
        state.selectedTeam === 'all' ||
        Object.entries(resource).some(
          ([key, value]) =>
            key.toLowerCase().includes('team') &&
            key !== 'jira_team_name' &&
            normalizeTeamId(String(value)) === state.selectedTeam,
        );
      return matchesSearch && matchesTeam;
    });
  }, [failedResources, state.searchTerm, state.selectedTeam]);

  const sortedResources = useMemo(() => {
    return [...filteredResources].sort((a, b) => {
      const rawA = a[activeSortCol];
      const rawB = b[activeSortCol];
      const parseNum = (v: any) =>
        parseFloat(String(v ?? '').replace(/%$/, ''));
      const numA = parseNum(rawA);
      const numB = parseNum(rawB);
      const bothNumeric = !Number.isNaN(numA) && !Number.isNaN(numB);
      let cmp: number;
      if (bothNumeric) {
        if (rawA === -1 && rawB !== -1) return 1;
        if (rawB === -1 && rawA !== -1) return -1;
        cmp = numA - numB;
      } else {
        cmp = String(rawA ?? '').localeCompare(String(rawB ?? ''));
      }
      return activeSortDir === 'desc' ? -cmp : cmp;
    });
  }, [filteredResources, activeSortCol, activeSortDir]);

  const totalPages = Math.ceil(sortedResources.length / ITEMS_PER_PAGE);
  const startIndex = (state.currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResources = sortedResources.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );
  const previewResources = sortedResources.slice(0, 2);

  const pushedAtStats = useMemo(() => {
    const withDate = filteredResources.filter(r => r.pushed_at);
    if (!withDate.length) return null;
    let over6m = 0;
    let over1y = 0;
    withDate.forEach(r => {
      const d = daysSince(r.pushed_at);
      if (d === null) return;
      if (d >= ONE_YEAR_DAYS) over1y += 1;
      if (d >= SIX_MONTHS_DAYS) over6m += 1;
    });
    return over6m > 0 || over1y > 0 ? { over6m, over1y } : null;
  }, [filteredResources]);

  const pushedAtChips = pushedAtStats ? (
    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
      {pushedAtStats.over6m > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<WarningIcon style={{ fontSize: '0.9rem' }} />}
          label={`${pushedAtStats.over6m} not pushed in >6 months`}
          style={{ height: 24, fontSize: '0.72rem' }}
        />
      )}
      {pushedAtStats.over1y > 0 && (
        <Chip
          size="small"
          color="error"
          icon={<WarningIcon style={{ fontSize: '0.9rem' }} />}
          label={`${pushedAtStats.over1y} not pushed in >1 year`}
          style={{ height: 24, fontSize: '0.72rem' }}
        />
      )}
    </Box>
  ) : null;

  const sharedTableHeadProps = {
    resourceColKey,
    tableColumns,
    activeSortCol,
    activeSortDir,
    onSort: state.handleSort,
    isVelocity,
    firstCheckName: sortedResources[0]?._checkName ?? check.checkName,
  };

  const sharedRowProps = {
    resourceNameKey,
    tableColumns,
    FixComponent,
    entity,
    performDataRefresh,
    isVelocity,
    teamNames,
  };

  const handleExportCsv = () => {
    const csv = buildRowsCsv(sortedResources, [resourceColKey]);
    if (!csv) return;
    const namePart = slugify(categoryName ?? '') || 'category';
    const checkPart = slugify(check.label ?? check.checkName ?? 'check');
    downloadCsv(`${namePart}-${checkPart}.csv`, csv);
  };

  const failedCount = isKTLO ? ktloDetails.length : failedResources.length;

  return (
    <Box key={check.checkName}>
      <Paper
        className={`${classes.checkList} ${
          !check.passed ? classes.checkHeader : ''
        }`}
        onClick={() => !check.passed && onToggle()}
        sx={{
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIconForCheck(check.passed)}
          <Box display="flex" alignItems="center">
            <Typography component="span" style={{ fontSize: '0.9rem' }}>
              {check.label}
            </Typography>
            {check.description && (
              <Tooltip title={check.description} arrow>
                <InfoIcon fontSize="small" className={classes.infoIcon} />
              </Tooltip>
            )}
          </Box>
        </Box>
        {!check.passed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              color="error"
              size="small"
              icon={<InfoIcon style={{ fontSize: '0.95rem' }} />}
              label={`${failedCount} ${getSubStrForLength(failedCount)} action`}
              style={{ height: 28, fontSize: '0.75rem' }}
            />
            <IconButton size="small">
              {isExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        )}
      </Paper>

      <Collapse in={!check.passed && isExpanded}>
        <Box className={classes.expandableSection}>
          <Box className={classes.expandableContent}>
            {(() => {
              if (isKTLO) {
                return (
                  <TableContainer className={classes.tableContainer}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Jira Team</TableCell>
                          <TableCell>Total Issues</TableCell>
                          <TableCell>KTLO Issues</TableCell>
                          <TableCell>KTLO %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ktloDetails.map((row: any, idx: number) => (
                          <TableRow key={idx} className={classes.tableRow}>
                            <TableCell>
                              <Box
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  className={classes.detailValue}
                                >
                                  {row.jira_team_name ?? 'N/A'}
                                </Typography>
                                {row.issue_url && (
                                  <Tooltip title="Open issue in Jira" arrow>
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={row.issue_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ padding: 2 }}
                                      onClick={(e: React.MouseEvent) =>
                                        e.stopPropagation()
                                      }
                                    >
                                      <OpenInNewIcon
                                        style={{ fontSize: '0.85rem' }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className={classes.detailValue}
                              >
                                {row.total_issue_count ?? 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className={classes.detailValue}
                              >
                                {row.ktlo_issue_count ?? 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className={classes.detailValue}
                              >
                                {row.ktlo_percentage !== null &&
                                row.ktlo_percentage !== undefined
                                  ? `${row.ktlo_percentage.toFixed(1)}%`
                                  : 'N/A'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              }

              if (!state.showFullTable) {
                return (
                  <Box className={classes.previewSection}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: 8,
                      }}
                    >
                      PREVIEW ({previewResources.length} of{' '}
                      {filteredResources.length})
                    </Typography>
                    {pushedAtChips}
                    <Box style={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <CheckTableHead {...sharedTableHeadProps} />
                        <TableBody>
                          {previewResources.map((resource, idx) => (
                            <ResourceRow
                              key={idx}
                              resource={resource}
                              index={idx}
                              {...sharedRowProps}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                    {sortedResources.length > 2 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        fullWidth
                        className={classes.showMoreButton}
                        onClick={() => state.setShowFullTable(true)}
                        startIcon={<ExpandMoreIcon />}
                      >
                        Show all {filteredResources.length} resources
                      </Button>
                    )}
                  </Box>
                );
              }

              return (
                <>
                  <Box className={classes.alertBox}>
                    <WarningIcon color="inherit" />
                    <Typography variant="body2" style={{ fontSize: '0.85rem' }}>
                      These resources are failing the{' '}
                      <strong>{check.label}</strong> check. Use filters to
                      narrow results.
                    </Typography>
                  </Box>

                  {pushedAtChips}

                  <Box className={classes.searchFilterBar}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Search resources..."
                      value={state.searchTerm}
                      onChange={e => state.setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      value={state.selectedTeam}
                      onChange={e => state.setSelectedTeam(e.target.value)}
                      style={{ minWidth: 180 }}
                      label="Team"
                    >
                      <MenuItem value="all">All teams</MenuItem>
                      {availableTeams.map(teamId => (
                        <MenuItem key={teamId} value={teamId}>
                          {teamNames[teamId] ?? teamId}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Tooltip
                      title="Export the rows below to CSV (respects current filters)"
                      arrow
                    >
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<GetAppIcon />}
                          onClick={handleExportCsv}
                          disabled={sortedResources.length === 0}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Export CSV
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  {filteredResources.length === 0 ? (
                    <Box className={classes.emptyState}>
                      <Typography color="textSecondary">
                        No resources found matching your search
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <TableContainer className={classes.tableContainer}>
                        <Table size="small">
                          <CheckTableHead {...sharedTableHeadProps} />
                          <TableBody>
                            {paginatedResources.map((resource, idx) => (
                              <ResourceRow
                                key={startIndex + idx}
                                resource={resource}
                                index={startIndex + idx}
                                {...sharedRowProps}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {totalPages > 1 && (
                        <Box className={classes.paginationContainer}>
                          <Pagination
                            count={totalPages}
                            page={state.currentPage}
                            onChange={(_, page) => state.setCurrentPage(page)}
                            color="primary"
                            showFirstButton
                            showLastButton
                            size="small"
                          />
                        </Box>
                      )}
                    </>
                  )}

                  <Box className={classes.footerStats}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        Showing {startIndex + 1}–
                        {Math.min(
                          startIndex + ITEMS_PER_PAGE,
                          filteredResources.length,
                        )}{' '}
                        of {filteredResources.length} filtered resources
                        {filteredResources.length !== failedResources.length &&
                          ` (${failedResources.length} total)`}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => state.setShowFullTable(false)}
                        startIcon={<ExpandLessIcon />}
                      >
                        Show less
                      </Button>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export const CategoryDetails = ({
  categoryScore,
  children,
  onClose,
  entity: propEntity,
  performDataRefresh,
}: {
  categoryScore?: CategoryScore & {
    team_id?: string;
    currentTeamScorePercentage?: number;
    currentTeamPassed?: number;
    currentTeamTotal?: number;
    aggregatedPassed?: number;
    aggregatedTotal?: number;
    refreshPeriodSeconds?: number;
    lastRefreshedDate?: string;
  };
  children?: ChildTeam[];
  onClose?: () => void;
  entity?: any;
  performDataRefresh?: () => void;
}) => {
  const classes = useStyles();
  const FixComponent = useGetFixAction(
    categoryScore?.category as FixableCategory,
  );
  const buildLink = useScorecardLinkBuilder();
  const entity = propEntity;

  const totalResources = categoryScore?.aggregatedTotal ?? 0;
  const passedResources = categoryScore?.aggregatedPassed ?? 0;
  const failedResourcesCount =
    totalResources > 0 ? totalResources - passedResources : 0;

  const isKTLO = isKTLOCategory(categoryScore?.category);

  const ktloStats = useMemo(() => {
    if (!isKTLO) return null;
    let totalKtloIssues = 0;
    let totalIssues = 0;
    categoryScore?.checks?.forEach(check => {
      const details =
        check.checkDetails?.length > 0 ? check.checkDetails : [check];
      details.forEach((detail: any) => {
        if (
          detail.ktlo_issue_count !== null &&
          detail.ktlo_issue_count !== undefined
        )
          totalKtloIssues += detail.ktlo_issue_count;
        if (
          detail.total_issue_count !== null &&
          detail.total_issue_count !== undefined
        )
          totalIssues += detail.total_issue_count;
      });
    });
    return {
      totalKtloIssues,
      totalIssues,
      nonKtloIssues: totalIssues - totalKtloIssues,
    };
  }, [isKTLO, categoryScore]);

  const checks = useMemo(
    () => categoryScore?.checks ?? [],
    [categoryScore?.checks],
  );

  const firstFailedCheck = checks.find(c => !c.passed)?.checkName ?? null;
  const [expandedCheck, setExpandedCheck] = useState<string | null>(
    firstFailedCheck,
  );

  useEffect(() => {
    if (!expandedCheck) return;
    const stillFailed = checks.some(
      c => c.checkName === expandedCheck && !c.passed,
    );
    if (!stillFailed)
      setExpandedCheck(checks.find(c => !c.passed)?.checkName ?? null);
  }, [checks, expandedCheck]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const allTeamIds = useMemo(() => {
    const ids = new Set<string>();
    children?.forEach(child => {
      if (child.team_id) ids.add(normalizeTeamId(child.team_id));
    });
    checks.forEach(check => {
      check.checkDetails
        .flatMap(flattenCheckDetail)
        .filter(cd => cd.check_status === 'failed')
        .forEach(resource => {
          Object.entries(resource).forEach(([key, value]) => {
            if (
              key.toLowerCase().includes('team') &&
              key !== 'jira_team_name' &&
              value
            )
              ids.add(String(value).toLowerCase());
          });
        });
    });
    return Array.from(ids);
  }, [children, checks]);

  const teamNames = useTeamNames(allTeamIds);

  const checkAvailableTeams = useMemo(() => {
    const map: Record<string, string[]> = {};
    checks.forEach(check => {
      const teams = new Set<string>();
      check.checkDetails
        .flatMap(flattenCheckDetail)
        .filter(r => r.check_status === 'failed')
        .forEach(resource => {
          Object.entries(resource).forEach(([key, value]) => {
            if (
              key.toLowerCase().includes('team') &&
              key !== 'jira_team_name' &&
              value
            )
              teams.add(normalizeTeamId(String(value)));
          });
        });
      map[check.checkName] = Array.from(teams);
    });
    return map;
  }, [checks]);

  const handleDataRefresh = async () => {
    if (!performDataRefresh) return;
    setSnackbarOpen(false);
    await performDataRefresh();
    setSnackbarOpen(true);
  };

  const hasChildren = !!children?.length;

  return (
    <>
      <Paper className={classes.categoryCard}>
        <Box
          sx={{
            mb: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography
              variant="h5"
              style={{ fontSize: '1.2rem', fontWeight: 600 }}
            >
              {categoryScore?.category}
            </Typography>
            <Typography variant="caption" className={classes.descriptionText}>
              {categoryScore?.description}
            </Typography>
            {formatPeriodRange(
              categoryScore?.periodStart,
              categoryScore?.periodEnd,
            ) && (
              <Box className={classes.periodBadge}>
                <DateRangeIcon style={{ fontSize: '1rem' }} />
                Showing data for{' '}
                {formatPeriodRange(
                  categoryScore?.periodStart,
                  categoryScore?.periodEnd,
                )}
              </Box>
            )}
            {categoryScore?.refreshPeriodSeconds !== undefined &&
              categoryScore?.lastRefreshedDate && (
                <Box
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Last refreshed: {categoryScore.lastRefreshedDate}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    · Cadence:{' '}
                    {formatRefreshPeriod(categoryScore.refreshPeriodSeconds)}
                  </Typography>
                </Box>
              )}
          </Box>
          {onClose && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        <Box className={classes.categoryCardContent}>
          <Box className={classes.categoryCardContentLeft}>
            {!isKTLO && (
              <>
                <StatBox pass value={passedResources} label="Passed" />
                <StatBox
                  pass={false}
                  value={failedResourcesCount}
                  label="Failed"
                />
                <Typography
                  variant="body2"
                  style={{ fontSize: '0.85rem', marginLeft: 10 }}
                >
                  {passedResources}/{totalResources} resources passing
                </Typography>
              </>
            )}
            {isKTLO && ktloStats && (
              <Box style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <StatBox
                  pass
                  value={ktloStats.nonKtloIssues}
                  label="Non-KTLO"
                />
                <StatBox
                  pass={false}
                  value={ktloStats.totalKtloIssues}
                  label="KTLO"
                />
                <Typography
                  variant="body2"
                  style={{ fontSize: '0.85rem', marginLeft: 10 }}
                >
                  {ktloStats.nonKtloIssues} non-KTLO /{' '}
                  {ktloStats.totalKtloIssues} KTLO out of{' '}
                  {ktloStats.totalIssues} total issues
                </Typography>
              </Box>
            )}
          </Box>
          <Box className={classes.scoreBox}>
            <Typography
              style={{
                fontSize: '3rem',
                fontWeight: 700,
                lineHeight: 1,
                marginBottom: 4,
              }}
            >
              {categoryScore?.score}%
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ fontSize: '0.75rem' }}
            >
              Overall Score
            </Typography>
          </Box>
        </Box>
      </Paper>
      <InfoCard title="Details" variant="gridItem" className={classes.mainCard}>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          message="Data refreshed successfully!"
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          style={{ marginTop: 32 }}
        />

        <Box
          sx={{
            overflowY: 'auto',
            paddingRight: 1,
            maxHeight: 'calc(100vh - 320px)',
          }}
        >
          {hasChildren && categoryScore && (
            <Box className={classes.childrenSection}>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 8,
                }}
              >
                <Box flex={1}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Direct Team Score
                  </Typography>
                  <Typography className={classes.sectionHelperText}>
                    Score is based only on resources directly owned by this
                    team, excluding child organizations.
                  </Typography>
                </Box>
                <Box style={{ textAlign: 'right', minWidth: 90 }}>
                  <Typography
                    style={{
                      fontSize: '2.2rem',
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                    color={getChildTeamScoreColor(categoryScore.tier || 'N/A')}
                  >
                    {displayValue(
                      categoryScore.currentTeamScorePercentage,
                      true,
                    )}
                  </Typography>
                  {categoryScore.currentTeamPassed !== null &&
                  categoryScore.currentTeamPassed !== undefined &&
                  categoryScore.currentTeamTotal !== null &&
                  categoryScore.currentTeamTotal !== undefined &&
                  !isKTLO &&
                  !(
                    categoryScore.currentTeamPassed === -1 &&
                    categoryScore.currentTeamTotal === -1
                  ) ? (
                    <Box
                      style={{
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'flex-end',
                        marginTop: 6,
                      }}
                    >
                      <PassFailCounts
                        passed={categoryScore.currentTeamPassed}
                        failed={
                          categoryScore.currentTeamTotal -
                          categoryScore.currentTeamPassed
                        }
                      />
                    </Box>
                  ) : (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ fontSize: '0.7rem', marginTop: 6 }}
                    >
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {hasChildren && (
            <Box className={classes.childrenSection}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Child Organizations
              </Typography>
              <Typography className={classes.sectionHelperText}>
                First-level child organizations. Click a card for detailed
                metrics.
              </Typography>
              <Grid container spacing={1.25}>
                {children!.map(child => {
                  const teamId = normalizeTeamId(child.team_id);
                  const teamName = teamNames[teamId] || child.team_id;
                  const childPassed =
                    child.aggregated_category_score_proportion_dividend;
                  const childTotal =
                    child.aggregated_category_score_proportion_denominator;
                  const hasValidCounts =
                    childPassed !== null &&
                    childPassed !== undefined &&
                    childTotal !== null &&
                    childTotal !== undefined &&
                    !(childPassed === -1 && childTotal === -1);
                  // SUP_INVALID has no team page: numbers only, not clickable
                  const isInvalidSupOrg = isInvalidSupOrgTeamId(child.team_id);
                  const card = (
                    <Paper
                      className={
                        isInvalidSupOrg
                          ? `${classes.childTeamCard} ${classes.childTeamCardStatic}`
                          : classes.childTeamCard
                      }
                      elevation={0}
                    >
                      <Box className={classes.childTeamHeader}>
                        <Box flex={1} minWidth={0}>
                          <Tooltip title={teamName} arrow placement="top">
                            <Typography className={classes.childTeamName}>
                              {teamName}
                            </Typography>
                          </Tooltip>
                          <Tooltip
                            title={child.team_id}
                            arrow
                            placement="bottom"
                          >
                            <Typography className={classes.childTeamId}>
                              {child.team_id}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <Box>
                          <Typography
                            className={classes.childTeamScore}
                            color={getChildTeamScoreColor(
                              child.aggregated_category_badge,
                            )}
                          >
                            {displayValue(
                              child.aggregated_category_score_percentage,
                              true,
                            )}
                          </Typography>
                          {hasValidCounts && !isKTLO ? (
                            <PassFailCounts
                              passed={childPassed!}
                              failed={childTotal! - childPassed!}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{ marginTop: 3, fontSize: '0.7rem' }}
                            >
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {!isInvalidSupOrg && (
                        <Box className={classes.childTeamBadge}>
                          <Chip
                            label={child.aggregated_category_badge}
                            color={getBadgeColor(
                              child.aggregated_category_badge,
                            )}
                            size="small"
                          />
                        </Box>
                      )}
                    </Paper>
                  );
                  return (
                    <Grid item xs={12} sm={6} md={3} key={child.team_id}>
                      {isInvalidSupOrg ? (
                        card
                      ) : (
                        <Link
                          href={buildLink(
                            `/catalog/default/group/${teamId}/scorecards`,
                            { category: categoryScore?.category ?? '' },
                          )}
                          underline="none"
                        >
                          {card}
                        </Link>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
          {!!checks.length && (
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Checks
              </Typography>
              <Typography className={classes.sectionHelperText}>
                {isKTLO
                  ? 'Below table shows the investment category distribution for the current period.'
                  : 'Failed checks are expandable. The first failed check is automatically expanded.'}
              </Typography>
              {checks.map((check: CheckV2) => (
                <CheckRow
                  key={check.checkName}
                  check={check}
                  isExpanded={expandedCheck === check.checkName}
                  onToggle={() =>
                    setExpandedCheck(prev =>
                      prev === check.checkName ? null : check.checkName,
                    )
                  }
                  isVelocity={
                    categoryScore?.category?.toLowerCase() === 'velocity'
                  }
                  isKTLO={isKTLO}
                  FixComponent={FixComponent}
                  entity={entity}
                  performDataRefresh={handleDataRefresh}
                  availableTeams={checkAvailableTeams[check.checkName] ?? []}
                  teamNames={teamNames}
                  categoryName={categoryScore?.category}
                />
              ))}
            </Box>
          )}
        </Box>
      </InfoCard>
    </>
  );
};
